<template>
    <div class="space-y-6">
        <div class="bg-gray-50 rounded-lg">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Review Your Information</h3>
            <p class="text-sm text-gray-600 mb-6">Please review all information below before submitting your application.</p>
            
            <!-- Personal Information Summary -->
            <div class="overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Personal Information</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide your basic personal information.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Full Name</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.first_name }} {{ form.middle_name }} {{ form.last_name }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Phone</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.phone }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Birthday</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ formatDate(form.dob) }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">SSN (last 4)</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">***-**-<span v-if="showSSN">{{ form.ssn }}</span><span v-else>****</span> <font-awesome-icon :icon="faEye" class="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" @click="toggleSSNVisibility"></font-awesome-icon></dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Desired Move-in Date</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ formatDate(form.desired_move_in_date) }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Desired Lease Term</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ formatLeaseterm(form.desired_lease_term) }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Monthly Rent Amount</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ formatCurrency(property.rent) }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100 pr-16">I'm willing to increase the monthly rent for this property</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300"><span v-if="form.willing_rent">{{ formatCurrency(form.willing_rent) }}</span><span v-else>No increase</span></dd>
                        </div>
                    </dl>
                </div>
            </div>
            
            <!-- Current Address Summary -->
             <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Current Address</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide your current address.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Address 1</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.address_1 }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Address 2</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ (form.address_2) ? form.address_2 : 'N/A' }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">City</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.city }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">State</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.state }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Zipcode</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.zip_code }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Own or Rent?</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.current_own_rent == 1 ? 'Own' : (form.current_own_rent == 2 ? 'Rent' : 'Not selected') }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Current Landlord Name</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.landlord_name }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100 pr-16">Current Landlord Phone</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.landlord_phone }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100 pr-16">Current Landlord Email</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.landlord_email }}</dd>
                        </div>
                    </dl>
                </div>
            </div>

            <!-- Previous Address Summary -->
            <div v-if="hasPreviousAddress" class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Previous Address</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide your previous address.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Address 1</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_address_1 }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Address 2</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ (form.previous_address_2) ? form.previous_address_2 : 'N/A' }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">City</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_city }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">State</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_state }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Zipcode</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_zip_code }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Own or Rent?</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_own_rent == 1 ? 'Own' : (form.previous_own_rent == 2 ? 'Rent' : 'Not selected') }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Previous Landlord Name</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_landlord_name }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100 pr-16">Previous Landlord Phone</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_landlord_phone }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100 pr-16">Previous Landlord Email</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.previous_landlord_email }}</dd>
                        </div>
                    </dl>
                </div>
            </div>
            
            <!-- Employment Summary -->
            <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Employment & Source of Income</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide information about your employment and income.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Company Name</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.source_of_income_company_name }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Position/Job Title</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ (form.source_of_income_position) ? form.source_of_income_position : 'N/A' }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Annual Income</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.source_of_income_annual_income }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Supervisor Name</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.source_of_income_supervisor_name }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Supervisor Phone</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.source_of_income_supervisor_phone }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Supervisor Email</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.source_of_income_supervisor_email }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">How long have you worked here?</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.source_of_income_years || 0 }} years {{ form.source_of_income_months || 0 }} months</dd>
                        </div>

                        <div v-if="form.user_incomes && form.user_incomes.length && hasAdditionalIncomeSource" class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Additional Income Sources</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">
                                <ul role="list" class="divide-y divide-gray-100 rounded-md border border-gray-200 dark:divide-white/5 dark:border-white/10">
                                    <li v-for="(income, index) in form.user_incomes" :key="index" class="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6">
                                        <div class="flex w-0 flex-1 items-center">
                                            <div class="ml-4 flex min-w-0 flex-1 gap-2">
                                                <span class="truncate font-medium text-gray-900 dark:text-white">{{ income.type }}</span>
                                                <span class="shrink-0 text-gray-800 font-semibold dark:text-gray-500 ml-8">{{ formatCurrency(income.amount) }}</span>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </dd>
                        </div>
                        <dl v-else class="divide-y divide-gray-100 dark:divide-white/5">
                            <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Additional Income Sources</dt>
                                <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">You provided no additional income sources.</dd>
                            </div>
                        </dl>
                    </dl>
                </div>
            </div>

            <!-- Vehicles -->
            <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Vehicles</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide information about your vehicles.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl v-if="form.vehicles && form.vehicles.length && hasVehicleData" class="divide-y divide-gray-100 dark:divide-white/5">
                        <div v-for="(vehicle, index) in form.vehicles" :key="index" class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Vehicle # {{ index + 1 }}</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ vehicle.model }} {{ vehicle.color }} {{ vehicle.year }}</dd>
                        </div>
                    </dl>
                    <dl v-else class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Vehicles</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">You provided no vehicles.</dd>
                        </div>
                    </dl>
                </div>
            </div>
            
            <!-- Additional Applicants -->
            <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Additional Applicants</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide information about your additional applicants.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl v-if="form.additional_applicants && form.additional_applicants.length && hasAdditionalApplicantData" class="divide-y divide-gray-100 dark:divide-white/5">
                        <div v-for="(applicant, index) in form.additional_applicants" :key="index" class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Applicant # {{ index + 1 }}</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ applicant.name }} {{ applicant.email }}</dd>
                        </div>
                    </dl>
                    <dl v-else class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Additional Applicants</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">You provided no additional applicants.</dd>
                        </div>
                    </dl>
                </div>
            </div>

            <!-- Agent Information -->
            <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Your Agent</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide information about your agent.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl v-if="form.name_agent_representing || form.email_agent_representing" class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Name</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.name_agent_representing }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Email</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{{ form.email_agent_representing }}</dd>
                        </div>
                    </dl>
                     <dl v-else class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">No Agent Provided</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">You provided no agent information.</dd>
                        </div>
                    </dl>
                </div>
            </div>
            
            <!-- Animals -->
            <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Animals</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide information about your animals.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl v-if="form.pets" class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Name</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300 whitespace-pre-line">{{ form.pets }}</dd>
                        </div>
                    </dl>
                     <dl v-else class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">No Animals Provided</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">You provided no animal information.</dd>
                        </div>
                    </dl>
                </div>
            </div>

            <!-- Additional Occupants -->
            <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Additional Occupants</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide information about your additional occupants.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl v-if="form.additional_occupants" class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Additional Occupants</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300 whitespace-pre-line">{{ form.additional_occupants }}</dd>
                        </div>
                    </dl>
                     <dl v-else class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">No Additional Occupants Provided</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">You provided no additional occupant information.</dd>
                        </div>
                    </dl>
                </div>
            </div>

            <!-- Additional Information -->
            <div class="mt-6 overflow-hidden bg-white shadow sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div class="px-4 py-6 sm:px-6">
                    <h3 class="text-base/7 font-semibold text-gray-900 dark:text-white">Additional Information</h3>
                    <p class="mt-1 max-w-2xl text-sm/6 text-gray-500 dark:text-gray-300">Please provide any additional information.</p>
                </div>
                <div class="border-t border-gray-100 dark:border-white/5">
                    <dl v-if="form.additional_information" class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">Additional Information</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300 whitespace-pre-line">{{ form.additional_information }}</dd>
                        </div>
                    </dl>
                     <dl v-else class="divide-y divide-gray-100 dark:divide-white/5">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt class="text-sm font-medium text-gray-900 dark:text-gray-100">No Additional Information Provided</dt>
                            <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">You provided no additional information.</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
export default {
    components: {
        FontAwesomeIcon
    },
    props: {
        form: Object,
        application: Object,
        states: Object,
        validateField: Function,
        applicant_type: Object,
        property: Object
    },
    data() {
        return {
            showSSN: false,
            faEye: faEye
        }
    },
    computed: {
        hasPreviousAddress() {
            return this.form.previous_address_1 || this.form.previous_city || this.form.previous_state;
        },
        hasVehicleData() {
            return this.form.vehicles && this.form.vehicles.some(vehicle => 
                vehicle.model || vehicle.year || vehicle.color
            );
        },
        hasAdditionalIncomeSource() {
            return this.form.user_incomes && this.form.user_incomes.some(income => 
                income.source || income.amount
            );
        },
        hasAdditionalApplicantData() {
            return this.form.additional_applicants && this.form.additional_applicants.some(applicant => 
                applicant.name || applicant.email
            );
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return 'Not provided';
            
            // Handle date strings in YYYY-MM-DD format (from HTML date inputs)
            // Parse manually to avoid timezone conversion issues
            if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}/)) {
                const [year, month, day] = date.split('-').map(Number);
                // Create date in local timezone to avoid UTC conversion
                const localDate = new Date(year, month - 1, day);
                return localDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
            }
            
            // Handle date strings in M/D/YYYY or n/j/Y format (from backend accessor)
            if (typeof date === 'string' && date.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
                const [month, day, year] = date.split('/').map(Number);
                const localDate = new Date(year, month - 1, day);
                return localDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
            }
            
            // Fallback for other date formats
            const dateObj = new Date(date);
            // Check if date is valid
            if (isNaN(dateObj.getTime())) {
                return date; // Return as-is if not a valid date
            }
            return dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        },
        formatNumber(number) {
            if (!number) return '0';
            return parseInt(number).toLocaleString();
        },
        formatLeaseterm(term) {
            if (!term) return 'Not specified';
            // Convert kebab-case to proper case
            return term.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        },
        formatCurrency(amount) {
            if (!amount) {
                amount = 0;
            }
            amount = parseFloat(amount);
            return amount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            })
        },
        toggleSSNVisibility() {
            this.showSSN = !this.showSSN;
        }
    }
}
</script>